import { memo, type ReactNode } from "react";
import { type GoogleMarker } from "~/domains/salesPartner/types/SalesPartnerTypes";
import GmapsCardRightUnregistered from "~/features/salesPartner/components/CompanyDetailsCard/SPCompanyDetailsCardRightUnregistered";
import GmapsCardRightRegistered from "~/features/salesPartner/components/CompanyDetailsCard/SPCompanyDetailsCardRightRegistered";
import { userIsLoggedIn, userIsRegistered } from "~/domains/user/utils/roles";
import classNames from "classnames";
import { Competences, SPCompanyDetails } from "./SPCompanyDetails";
import { useTranslation } from "react-i18next";
import { Button, IconButton, styled } from "~/common/mui";
import classes from "./SPCompanyDetailsCard.module.less";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { useGuestCheckoutDialog } from "~/features/cart/B2CCart/components/GuestCheckoutDialog/useGuestCheckoutDialog";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

interface Props {
  className?: string;
  selectedMarker: GoogleMarker;
  activeTab: string;
  shareLinearConfigurationData?: string[];
  action?: ReactNode;
  openRegisterSnackbar?: () => void;
  isCheckout?: boolean;
  onClose?: () => void;
}

export const SPCompanyDetailsCard = memo((props: Props) => {
  const { selectedMarker, activeTab, className, isCheckout, onClose } = props;

  const { t } = useTranslation();

  const isDesktop = useIsMinDesktopSize();

  const loggedIn = userIsLoggedIn();
  const registeredUser = userIsRegistered();

  const cls = classNames(
    "scha__sp__details-card",
    isCheckout && classes.reducedPadding,
    !isDesktop && classes.mobileStyling,
    className,
  );

  const [handleB2CCheckout, guestCheckoutDialog] = useGuestCheckoutDialog();

  return (
    <div className={cls}>
      {onClose && (
        <StyledIconButton variant="contained" color={"secondary"} size={"tiny"} onClick={() => onClose()}>
          <CloseRoundedIcon />
        </StyledIconButton>
      )}
      <div
        className={classNames(
          "scha__sp__details-card__content",
          isCheckout && "scha__sp__details-card__content-with-gap",
        )}
      >
        {guestCheckoutDialog}
        <SPCompanyDetails
          openRegisterSnackbar={props.openRegisterSnackbar}
          className={classNames(!isCheckout && "scha__sp__details-card__left")}
          action={props.action}
          company={selectedMarker}
          activeTab={activeTab}
          isCheckout={isCheckout}
        />

        {!isCheckout ? (
          <div className="scha__sp__details-card__right">
            {!loggedIn && <GmapsCardRightUnregistered company={selectedMarker} />}
            {registeredUser && (
              <GmapsCardRightRegistered
                shareLinearConfigurationData={props.shareLinearConfigurationData}
                companyDetails={selectedMarker.address}
                company={selectedMarker}
              />
            )}
          </div>
        ) : (
          <div className="scha__sp__details-card__right-loggedIn">
            {Competences(selectedMarker.certifiedCompetences, selectedMarker.productCompetences)}
          </div>
        )}
      </div>
      {isDesktop && isCheckout && (
        <div className="scha__sp__details-card__right-loggedIn__confirm">
          <Button onClick={() => handleB2CCheckout(selectedMarker.subsidiaryId)}>
            {t("partnerFinder.search.confirm")}
          </Button>
        </div>
      )}
    </div>
  );
});

const StyledIconButton = styled(IconButton)({
  position: "absolute",
  right: "16px",
  top: "16px",
});
